import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Loading } from '../components/Loading';
import { CoverImage } from '../components/CoverImage';
import type { Novel } from '../services/types';

function NovelCard({ novel }: { novel: Novel }) {
  return (
    <div className="card-interactive overflow-hidden">
      <Link to={`/novels/${novel.id}`}>
        <div className="aspect-[3/4] bg-neutral-100 dark:bg-neutral-800 relative">
          <CoverImage
            src={novel.cover_url}
            alt={novel.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </Link>
      <div className="p-3">
        <Link to={`/novels/${novel.id}`}>
          <h3 className="font-medium text-neutral-900 dark:text-neutral-50 line-clamp-2 text-sm leading-tight mb-1 hover:text-primary-500 transition-colors">
            {novel.title}
          </h3>
        </Link>
        {novel.author && (
          <Link
            to={`/author/${encodeURIComponent(novel.author)}`}
            className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors truncate block"
            onClick={(e) => e.stopPropagation()}
          >
            {novel.author}
          </Link>
        )}
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
          {novel.total_chapters} 章
        </p>
      </div>
    </div>
  );
}

export function HomePage() {
  const { isAdmin } = useAuth();
  const [novels, setNovels] = useState<Novel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 搜尋狀態
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Novel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    loadRandomNovels();
  }, []);

  const loadRandomNovels = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getRandomNovels(10);
      setNovels(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  // 防抖搜尋
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchMode(false);
      setSearchResults([]);
      return;
    }

    setSearchMode(true);
    setIsSearching(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await api.searchNovels(query.trim());
        setSearchResults(results);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchMode(false);
    setSearchResults([]);
  }, []);

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <h1 className="heading-display mb-3">小說收藏器</h1>
          <p className="body-base">
            探索精彩小說，享受閱讀時光
          </p>
        </div>

        {/* 搜尋欄 */}
        <div className="mb-10">
          <div className="relative max-w-md mx-auto">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="搜尋書籍或作者..."
              className="input pl-11 pr-10 rounded-full"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchMode && (
            <p className="text-center body-small mt-3">
              {isSearching ? '搜尋中...' : `找到 ${searchResults.length} 個結果`}
            </p>
          )}
        </div>

        {/* 標題和操作列 */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="heading-section">
            {searchMode ? '搜尋結果' : '隨機推薦'}
          </h2>
          <div className="flex gap-2">
            {!searchMode && (
              <button
                onClick={loadRandomNovels}
                disabled={loading}
                className="btn btn-ghost btn-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                換一批
              </button>
            )}
            <Link to="/novels" className="btn btn-primary btn-sm">
              查看書架
            </Link>
            {isAdmin && (
              <Link to="/admin/add-novel" className="btn btn-secondary btn-sm">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                新增小說
              </Link>
            )}
          </div>
        </div>

        {/* 內容區域 */}
        {searchMode ? (
          isSearching ? (
            <Loading text="搜尋中..." />
          ) : searchResults.length === 0 ? (
            <div className="card">
              <div className="card-body flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <p className="body-base mb-1">找不到符合的結果</p>
                <p className="body-small">試試其他關鍵字</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {searchResults.map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>
          )
        ) : loading ? (
          <Loading text="載入中..." />
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-danger-600 dark:text-danger-400 mb-4">{error}</p>
            <button onClick={loadRandomNovels} className="btn btn-primary btn-md">
              重試
            </button>
          </div>
        ) : novels.length === 0 ? (
          <div className="card">
            <div className="card-body flex flex-col items-center justify-center py-12 text-center">
              <p className="body-base mb-1">目前沒有已完成的小說</p>
              <p className="body-small">
                {isAdmin ? '請先新增並等待小說下載完成' : '請稍後再來查看'}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {novels.map((novel) => (
              <NovelCard key={novel.id} novel={novel} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
